/**
 * Whether the OCR results screen may re-open an already-verified
 * (`status === 'processed'`) field for editing.
 *
 * Accepting a field — or a whole page via "Accept all" — used to be final: the
 * card dropped its pencil and the only way back was a full re-run. Correcting
 * a typo an operator only noticed after approving it is normal work, so the
 * affordance stays for as long as the container belongs to the OCR stage:
 * before it is submitted to QA, and again after QA1 sends it back
 * (`qa_stage='qa_rejected'`, which `hasBeenSubmittedToQa` deliberately reads as
 * NOT submitted).
 *
 * Two boundaries stay closed: a container sitting inside a QA tier is the
 * reviewer's to correct (the QA detail page passes its own `allowEditVerified`,
 * routed through the QA-scoped endpoint), and a frozen history snapshot is
 * never editable.
 *
 * The backend has no QA-stage guard on `PUT /ocr/results/{id}/field`, so
 * `ocr.edit` is the only permission involved — hence `canEditOcr` is passed in
 * rather than derived here.
 */
export function canReopenVerifiedFields({
  isPreviewMode,
  hasBeenSubmittedToQa,
  canEditOcr
}: {
  /** Viewing a frozen run from history. */
  isPreviewMode: boolean;
  /** Container is in a QA tier (a QA1 reject does not count). */
  hasBeenSubmittedToQa: boolean;
  /** Holder of `ocr.edit` (or super admin). */
  canEditOcr: boolean;
}): boolean {
  return !isPreviewMode && !hasBeenSubmittedToQa && canEditOcr;
}
